import React from "react";
import Images from "../../Images";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

const CTASection = () => {
  const navigate = useNavigate();
  
  return (
    <section className="relative bg-gradient-to-br from-[#F4FBF5] via-white to-[#F4FBF5]">
      {/* Subtle background accent */}
      <div className="absolute inset-0">
        <div className="absolute left-0 top-0 w-1/2 h-full bg-[#DBF7E0]/20" />
        <div className="absolute right-0 bottom-0 w-96 h-96 bg-[#FF6C6C]/5 rounded-tl-full" />
      </div>

      <div className="relative max-w-[1400px] mx-auto flex flex-col lg:flex-row items-center min-h-[90vh] py-20 px-6 lg:px-12">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full lg:w-[50%] order-2 lg:order-1"
        >
          <div className="relative">
            <div className="absolute -inset-4 rounded-3xl backdrop-blur-sm bg-white/50" />
            <img
              src={Images.cta_img}
              alt="Dealboxx Platform Preview"
              className="object-contain relative z-10 w-full h-auto rounded-2xl shadow-xl"
            />
            {/* Decorative elements */}
            <div className="absolute -left-6 -bottom-6 w-24 h-24 bg-[#DBF7E0] rounded-full opacity-50" />
            <div className="absolute -right-4 -top-4 w-16 h-16 bg-[#FF6C6C]/20 rounded-full" />
          </div>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="w-full lg:w-[50%] lg:pl-16 order-1 lg:order-2 mb-12 lg:mb-0"
        >
          <div className="space-y-8">
            {/* Badge */}
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="inline-flex items-center px-4 py-2 bg-white rounded-full shadow-sm"
            >
              <span className="flex h-2 w-2 rounded-full bg-[#FF6C6C]" />
              <span className="ml-3 text-sm font-medium text-gray-600">
                Smart Business Solutions
              </span>
            </motion.div>

            {/* Main Content */}
            <div className="space-y-6">
              <h2 className="text-4xl font-bold leading-tight text-gray-900 md:text-5xl lg:text-6xl">
                Boost Your 
                <span className="relative whitespace-nowrap">
                  <span className="relative z-10"> Business </span>
                  <svg className="absolute left-0 -bottom-2 w-full" viewBox="0 0 200 20" fill="none">
                    <path d="M0 10 Q100 0 200 10" stroke="#FF6C6C" strokeWidth="4" strokeLinecap="round"/>
                  </svg>
                </span>
                with Dealboxx
              </h2>
              
              <div className="space-y-4">
                <p className="text-xl leading-relaxed text-gray-600">
                  Showcase, manage, and optimize all your discount deals and offers, 
                  reaching targeted customers ready to engage with your promotions.
                </p>
                
                <ul className="space-y-3">
                  {[
                    "Smart deal management",
                    "Real-time analytics",
                    "Customer engagement tools"
                  ].map((feature, index) => (
                    <li key={index} className="flex items-center text-gray-700">
                      <svg className="w-5 h-5 mr-3 text-[#FF6C6C]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-col gap-4 sm:flex-row">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate("/signin")}
                className="px-8 py-4 bg-[#FF6C6C] text-white font-medium rounded-xl 
                  shadow-lg hover:shadow-xl transition-all duration-200 flex items-center 
                  justify-center gap-2 group relative overflow-hidden"
              >
                <span className="flex relative z-10 gap-2 items-center">
                  Get Started
                  <svg 
                    className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-[#FF8B8B] to-[#FF6C6C] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </motion.button>
              
              <motion.button
              onClick={()=>navigate("/support")}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="px-8 py-4 border-2 border-gray-200 text-gray-700 font-medium 
                  rounded-xl hover:border-[#FF6C6C] hover:text-[#FF6C6C] 
                  transition-colors duration-200 bg-white/50 backdrop-blur-sm"
              >
                Contact Us
              </motion.button>
            </div>

            {/* Trust Indicators */}
            <div className="pt-8 border-t border-gray-100">
              <div className="flex gap-8 items-center">
                <div className="flex relative items-center">
                  {/* Glassmorphism circles */}
                  <div className="flex relative -space-x-4">
                    <div className="flex overflow-hidden justify-center items-center w-12 h-12 bg-gradient-to-br rounded-full border shadow-lg backdrop-blur-md from-white/80 to-white/20 border-white/30">
                      <img src={Images.vendor1 || 'https://picsum.photos/48'} alt="Vendor" className="object-cover w-8 h-8 rounded-full" />
                    </div>
                    <div className="flex overflow-hidden justify-center items-center w-12 h-12 bg-gradient-to-br rounded-full border shadow-lg backdrop-blur-md from-white/80 to-white/20 border-white/30">
                      <img src={Images.vendor2 || 'https://picsum.photos/49'} alt="Vendor" className="object-cover w-8 h-8 rounded-full" />
                    </div>
                    <div className="flex overflow-hidden justify-center items-center w-12 h-12 bg-gradient-to-br rounded-full border shadow-lg backdrop-blur-md from-white/80 to-white/20 border-white/30">
                      <img src={Images.vendor3 || 'https://picsum.photos/50'} alt="Vendor" className="object-cover w-8 h-8 rounded-full" />
                    </div>
                    <div className="w-12 h-12 rounded-full bg-gradient-to-tr from-[#FF6C6C]/30 to-[#FF6C6C]/10 backdrop-blur-md border border-white/30 shadow-lg flex items-center justify-center">
                      <span className="text-sm font-medium text-[#FF6C6C]">+</span>
                    </div>
                  </div>
                  {/* Decorative elements */}
                  <div className="absolute -z-10 -left-3 -top-3 w-16 h-16 bg-[#DBF7E0]/30 rounded-full blur-sm" />
                  <div className="absolute -z-10 -right-2 -bottom-2 w-12 h-12 bg-[#FF6C6C]/10 rounded-full blur-sm" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gray-900 to-gray-700">
                    500+
                  </p>
                  <p className="text-gray-600">Active vendors</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CTASection;
